import React, { ReactNode } from 'react';
import {
  Popover as SherpaPopover,
  PopoverArrow as SherpaPopoverArrow,
  PopoverContent as SherpaPopoverContent,
  PopoverHeadline as SherpaPopoverHeadline,
  PopoverText as SherpaPopoverText,
  PopoverCloseButton as SherpaPopoverCloseButton,
} from '../../../../deps/@bshgroup/component-popover';

export interface PopoverProps {
  /**
   * Popover title
   */
  title?: ReactNode;
  /**
   * Popover description
   */
  description?: ReactNode;
  /**
   * Popover arrow tip x position as string, e.g. 20px, 1rem, 50%, requires parent to be `position: relative`
   */
  x?: string;
  /**
   * Popover arrow tip y position as string, e.g. 20px, 1rem, 50%, requires parent to be `position: relative`
   */
  y?: string;
  /**
   * x position of the popover relative to the arrow tip
   */
  positionX?: 'left' | 'center' | 'right';
  /**
   * y position of the popover relative to the arrow tip
   */
  positionY?: 'top' | 'bottom';
  /**
   * Orientation of the arrow
   */
  arrowOrientation?: 'horizontal' | 'vertical';
  /**
   * Callback when the popover is closed, shows a close button if provided
   */
  onClose?: () => void;
}

const positionStyles = {
  arrow: {
    x: {
      left: {
        insetInlineEnd: 16,
      },
      center: {
        insetInlineStart: 'calc(50% - 8px)',
      },
      right: {
        insetInlineStart: 16,
      },
    },
    y: {
      top: {
        insetBlockEnd: -8,
      },
      bottom: {
        insetBlockStart: -8,
      },
    },
  },
  arrowPosition: {
    y: {
      top: 'bottom',
      bottom: 'top',
    },
  },
  translate: {
    x: {
      left: 'calc(-100% + 24px)',
      center: '-50%',
      right: '-24px',
    },
    y: {
      top: 'calc(-100% - 8px)',
      bottom: '8px',
    },
  },
};

/**
 * The `Popover`  component can be used to add a popover to a target element.
 */
export function Popover({
  title,
  description,
  x = '0',
  y = '0',
  positionX = 'right',
  positionY = 'bottom',
  arrowOrientation = 'horizontal',
  onClose,
  ...props
}: PopoverProps & Omit<Partial<React.HTMLAttributes<HTMLDivElement>>, keyof PopoverProps | 'content'>) {
  return (
    <SherpaPopover
      arrow={config => (
        <SherpaPopoverArrow
          {...config}
          direction={positionStyles.arrowPosition.y[positionY] as 'top' | 'bottom'}
          css={{
            position: 'absolute',
            writingMode: arrowOrientation === 'vertical' ? 'vertical-lr' : undefined,
            ...positionStyles.arrow.x[positionX],
            ...positionStyles.arrow.y[positionY],
          }}
        />
      )}
      content={config => (
        <SherpaPopoverContent
          {...config}
          size="sm"
          closeButton={() => (onClose ? <SherpaPopoverCloseButton onClick={onClose} /> : <></>)}
          headline={config => <SherpaPopoverHeadline {...config}>{title}</SherpaPopoverHeadline>}
          text={config =>
            description ? (
              <SherpaPopoverText css={{ opacity: 1, overflow: 'auto' }} {...config}>
                {description}
              </SherpaPopoverText>
            ) : (
              <></>
            )
          }
          css={{ height: '100%', '& > div:first-of-type': { alignItems: 'start' } }}
        />
      )}
      css={{
        position: 'absolute',
        left: x,
        top: y,
        transform: `translate(${positionStyles.translate.x[positionX]}, ${positionStyles.translate.y[positionY]})`,
      }}
      {...props}
    />
  );
}
